/**
 * @file overview-model.ts
 * @description Чистые правила «Обзора» платформы (phase19.29): сводки по
 * пользователям, интеграциям и копиям и список «Требует внимания».
 *
 * Всё считается из ответов уже существующих запросов (`listAdminUsers`,
 * `platformIntegrations.list`, `getBackupConfig` + `listBackupRuns`) — новых
 * серверных запросов нет. Правила вынесены из разметки, чтобы их можно было
 * проверить тестом без рендера.
 */

import type { AdminUser } from "../../../lib/api-admin-users";
import {
  backupHealthNotice,
  getBackupConfig,
  isRunStuck,
  isRunSummary,
  listBackupRuns,
  type BackupConfigDto,
  type BackupRunDto,
} from "../../../lib/api-backups";
import {
  INTEGRATION_KIND_META,
  type PlatformIntegrationDto,
} from "../../../lib/api-platform-integrations";

/** Сводка по пользователям платформы. */
export interface UsersSummary {
  active: number;
  disabled: number;
  /** Активные, которые ещё ни разу не входили. */
  neverLoggedIn: number;
}

export function summarizeUsers(users: AdminUser[]): UsersSummary {
  const active = users.filter((u) => u.isActive);
  return {
    active: active.length,
    disabled: users.length - active.length,
    neverLoggedIn: active.filter((u) => u.lastLoginAt === null).length,
  };
}

/** Сводка по экземплярам интеграций. */
export interface IntegrationsSummary {
  total: number;
  active: number;
  paused: number;
  errors: number;
}

export function summarizeIntegrations(items: PlatformIntegrationDto[]): IntegrationsSummary {
  const count = (status: PlatformIntegrationDto["status"]) =>
    items.filter((i) => i.status === status).length;
  return {
    total: items.length,
    active: count("ACTIVE"),
    paused: count("PAUSED"),
    errors: count("ERROR"),
  };
}

/** Данные плитки «Последняя копия»: настройки + последний запуск. */
export interface BackupOverview {
  config: BackupConfigDto;
  /** Последний итог запуска (`summary`), а если итогов нет — последний этап. */
  lastRun: BackupRunDto | null;
}

/** Сколько последних строк истории смотреть в поисках итога запуска. */
const RUNS_LOOKUP = 10;

/** Загрузка для плитки копий — два существующих запроса параллельно. */
export async function loadBackupOverview(): Promise<BackupOverview> {
  const [config, runs] = await Promise.all([
    getBackupConfig(),
    listBackupRuns({ limit: RUNS_LOOKUP }),
  ]);
  const lastRun = runs.items.find(isRunSummary) ?? runs.items[0] ?? null;
  return { config, lastRun };
}

/** Строка «Требует внимания». */
export interface AttentionItem {
  id: string;
  tone: "info" | "warning" | "error";
  text: string;
  /** Раздел, где проблему решают. */
  to: string;
}

const INTEGRATIONS_PATH = "/platform/integrations";
const BACKUPS_PATH = "/platform/backups";

/** Признаки проблем интеграций: экземпляры со статусом «ошибка». */
export function integrationAttention(items: PlatformIntegrationDto[]): AttentionItem[] {
  return items
    .filter((i) => i.status === "ERROR")
    .map((i) => ({
      id: `integration-${i.id}`,
      tone: "error" as const,
      text: `Интеграция «${INTEGRATION_KIND_META[i.kind].label}» (${i.name}) отвечает ошибкой`,
      to: INTEGRATIONS_PATH,
    }));
}

/** Признаки проблем с копиями: здоровье, сбой последнего запуска, диск, зависание. */
export function backupAttention(
  { config, lastRun }: BackupOverview,
  now: Date = new Date(),
): AttentionItem[] {
  const items: AttentionItem[] = [];
  const health = backupHealthNotice(config, "overview");
  if (health) {
    items.push({ id: "backup-health", tone: health.tone, text: health.text, to: BACKUPS_PATH });
  }
  if (config.lastRunStatus === "FAILED") {
    items.push({
      id: "backup-failed",
      tone: "error",
      text: "Последний запуск копирования завершился ошибкой",
      to: BACKUPS_PATH,
    });
  }
  if (config.diskWarning) {
    items.push({
      id: "backup-disk",
      tone: "warning",
      text: "На диске для копий заканчивается место",
      to: BACKUPS_PATH,
    });
  }
  if (lastRun && isRunStuck(lastRun, now)) {
    items.push({
      id: "backup-stuck",
      tone: "warning",
      text: "Запуск копирования не завершился больше суток",
      to: BACKUPS_PATH,
    });
  }
  return items;
}
